import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEmpresaAuth } from '../../../contexts/EmpresaAuthContext';
import Layout from '../../../components/shared/Layout/Layout';
import Loading from '../../../components/shared/Loading/Loading';
import ShareButtons from '../../../components/shared/ShareButtons/ShareButtons';
import { empresaShareUrl, empresaShareText } from '../../../utils/share';
import {
  DashboardContainer,
  WelcomeSection,
  VerificationNotice,
  StatsGrid,
  StatCard,
  StatNumber,
  StatLabel,
  QuickActions,
  ActionCard,
  RecentActivity,
  ActivityList,
  ActivityItem,
  EmptyState,
  PublicProfile,
} from './Dashboard.styles';

const Dashboard = () => {
  const { empresa, profileSync } = useEmpresaAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalActivities: 0,
    totalReservations: 0,
    todayReservations: 0,
    monthlyRevenue: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!empresa) {
      navigate('/empresa/login');
      return;
    }

    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa, navigate]);

  const loadDashboardData = async () => {
    try {
      const response = await fetch('/api/empresa/dashboard', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('empresa-token')}`,
        },
      });
      const data = await response.json();

      if (data.success) {
        setStats(data.stats);
        setRecentActivity(data.recentActivity);
      } else {
        console.error('Error loading dashboard:', data.error);
      }
    } catch (error) {
      console.error('Error connecting to server:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'var(--warning-dark)';
      case 'confirmed': return 'var(--primary)';
      case 'completed': return 'var(--success-dark)';
      case 'cancelled': return 'var(--danger)';
      default: return 'var(--text-muted)';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'reservation': return '📅';
      case 'qr_scan': return '📱';
      case 'payment': return '💰';
      default: return '📊';
    }
  };

  if (!empresa) {
    return <Loading text="Cargando..." />;
  }

  if (loading) {
    return (
      <Layout headerType="empresa">
        <Loading text="Cargando dashboard..." />
      </Layout>
    );
  }

  return (
    <Layout headerType="empresa">
      <DashboardContainer>
        <WelcomeSection>
          <div>
            <span className="eyebrow">Panel de control</span>
            <h1>Hola, {empresa.contactName}</h1>
            <p>{empresa.businessName}</p>
          </div>
          {empresa.verified ? (
            <span className="badge verified">✓ Empresa verificada</span>
          ) : (
            <span className="badge pending">⏳ Verificación pendiente</span>
          )}
        </WelcomeSection>

        {!empresa.verified && (
          <VerificationNotice>
            <strong>Tu empresa está en revisión.</strong>
            <p>
              Puedes crear tus actividades desde ya como borradores; podrás
              publicarlas en cuanto nuestro equipo verifique tu documentación.
            </p>
          </VerificationNotice>
        )}

        <StatsGrid>
          <StatCard>
            <StatNumber>{stats.totalActivities}</StatNumber>
            <StatLabel>Actividades activas</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.totalReservations}</StatNumber>
            <StatLabel>Total reservas</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.todayReservations}</StatNumber>
            <StatLabel>Reservas hoy</StatLabel>
          </StatCard>
          <StatCard $money>
            <StatNumber>
              ${stats.monthlyRevenue ? stats.monthlyRevenue.toLocaleString() : '0'}
            </StatNumber>
            <StatLabel>Ingresos del mes</StatLabel>
          </StatCard>
        </StatsGrid>

        <QuickActions>
          <h2>Acciones rápidas</h2>
          <div className="actions-grid">
            <ActionCard onClick={() => navigate('/empresa/activities')}>
              <div className="icon">🎯</div>
              <div>
                <h3>Gestionar actividades</h3>
                <p>Crear, editar y publicar tus actividades</p>
              </div>
            </ActionCard>

            <ActionCard onClick={() => navigate('/empresa/sales/new')}>
              <div className="icon">💵</div>
              <div>
                <h3>Vender en persona</h3>
                <p>Cobra en el lugar y entrega el QR al cliente</p>
              </div>
            </ActionCard>

            <ActionCard onClick={() => navigate('/empresa/sales')}>
              <div className="icon">🧾</div>
              <div>
                <h3>Ventas y corte de caja</h3>
                <p>Revisa, anula y exporta a CSV o Excel</p>
              </div>
            </ActionCard>

            <ActionCard onClick={() => navigate('/empresa/reservations')}>
              <div className="icon">📋</div>
              <div>
                <h3>Ver reservas</h3>
                <p>Gestionar todas las reservas</p>
              </div>
            </ActionCard>

            <ActionCard onClick={() => navigate('/empresa/scanner')}>
              <div className="icon">📱</div>
              <div>
                <h3>Escanear QR</h3>
                <p>Validar QRs y ver cuántos han entrado hoy</p>
              </div>
            </ActionCard>

            <ActionCard onClick={() => navigate('/empresa/promotores')}>
              <div className="icon">🤝</div>
              <div>
                <h3>Promotores</h3>
                <p>Acepta solicitudes, revisa ventas por promotor y paga sus cortes semanales</p>
              </div>
            </ActionCard>

            <ActionCard onClick={() => navigate('/empresa/promotor')}>
              <div className="icon">🔗</div>
              <div>
                <h3>Vender como promotor</h3>
                <p>Vende las actividades de otras empresas con tu enlace y gana comisión</p>
              </div>
            </ActionCard>

            <ActionCard onClick={() => navigate('/empresa/staff')}>
              <div className="icon">👥</div>
              <div>
                <h3>Equipo</h3>
                <p>Da acceso a tu personal para escanear QRs</p>
              </div>
            </ActionCard>

            <ActionCard onClick={() => navigate('/empresa/settings')}>
              <div className="icon">⚙️</div>
              <div>
                <h3>Configuración</h3>
                <p>Datos del negocio y preferencias</p>
              </div>
            </ActionCard>
          </div>
        </QuickActions>

        <PublicProfile>
          <h2>Tu perfil público</h2>
          {!empresa.slug ? (
            // Sesiones iniciadas antes de la fase 1 no traen el slug guardado:
            // se completa solo desde /api/empresa/settings (EmpresaAuthContext).
            profileSync === 'syncing' ? (
              <p>Cargando el enlace de tu perfil público…</p>
            ) : (
              <p>
                No pudimos cargar el enlace de tu perfil público. Recarga la página para intentarlo de nuevo.
              </p>
            )
          ) : !empresa.verified ? (
            <p>
              Tu perfil público estará disponible cuando tu empresa sea verificada.
            </p>
          ) : (
            <>
              <p>
                Comparte tu perfil con tus clientes:{' '}
                <a className="profile-link" href={`/empresas/${empresa.slug}`} target="_blank" rel="noopener noreferrer">
                  {window.location.origin}/empresas/{empresa.slug}
                </a>
              </p>
              <ShareButtons
                label="Compartir perfil"
                title={empresa.businessName}
                text={empresaShareText(empresa.businessName)}
                url={empresaShareUrl(empresa.slug)}
              />
            </>
          )}
        </PublicProfile>

        <RecentActivity>
          <h2>Actividad reciente</h2>
          {recentActivity.length > 0 ? (
            <ActivityList>
              {recentActivity.map(activity => (
                <ActivityItem key={activity.id}>
                  <div className="activity-icon">{getActivityIcon(activity.type)}</div>
                  <div className="activity-content">
                    <div className="activity-message">{activity.message}</div>
                    <div className="activity-time">
                      {activity.timestamp
                        ? new Date(activity.timestamp).toLocaleString()
                        : 'Ahora'}
                    </div>
                  </div>
                  <div
                    className="activity-status"
                    style={{ color: getStatusColor(activity.status) }}
                  >
                    ●
                  </div>
                </ActivityItem>
              ))}
            </ActivityList>
          ) : (
            <EmptyState>
              <p>Aún no hay actividad. Publica tu primera actividad para empezar a recibir reservas.</p>
            </EmptyState>
          )}
        </RecentActivity>
      </DashboardContainer>
    </Layout>
  );
};

export default Dashboard;
